import type { CustomerAccountsTranslations } from './types'

/**
 * English translations for CustomerAccounts domain
 */
export const enTranslations: CustomerAccountsTranslations = {
  // Page/Section level
  title: 'Customer Accounts',
  subtitle: 'Manage customer financial accounts and credit limits',

  // Actions specific to domain
  addCustomerAccount: 'Add Customer Account',
  editCustomerAccount: 'Edit Customer Account',
  deleteCustomerAccount: 'Delete Customer Account',

  // Search and filters
  searchPlaceholder: 'Search customer accounts...',
  noItemsFound: 'No customer accounts found',
  noItemsDescription: 'No customer accounts have been created yet. Add your first account to get started.',
  addFirst: 'Add your first customer account',

  // Form section
  form: {
    name: 'Account Name',
    namePlaceholder: 'Enter account name',
    status: 'Status',

    validation: {
      nameRequired: 'Account name is required',
    }
  },

  // Status and states specific to domain
  status: {
    active: 'Active',
    inactive: 'Inactive',
    suspended: 'Suspended',
    blocked: 'Blocked',
  },

  actions: {
    viewDetails: 'View Details',
    activate: 'Activate',
    suspend: 'Suspend',
    block: 'Block',
    adjustCreditLimit: 'Adjust Credit Limit',
  },

  // Toast messages for domain operations
  messages: {
    createSuccess: 'Customer account created successfully',
    updateSuccess: 'Customer account updated successfully',
    deleteSuccess: 'Customer account deleted successfully',
    deleteConfirm: 'Are you sure you want to delete this customer account?',
  },

  stats: {
    totalAccounts: 'Total Accounts',
    totalCreditLimit: 'Total Credit',
    totalOutstanding: 'Outstanding',
    totalOverdue: 'Overdue',
    accountsLabel: 'accounts',
  },

  filters: {
    searchAccounts: 'Search by account, tenant, or SAP BP...',
    status: 'Status',
    paymentTerms: 'Payment Terms',
    channel: 'Channel',
  },

  channel: {
    b2b: 'B2B',
    ecommerce: 'E-commerce',
  },

  paymentTerms: {
    cod: 'COD',
    prepaid: 'Prepaid',
    net_7: 'Net 7',
    net_15: 'Net 15',
    net_30: 'Net 30',
    net_60: 'Net 60',
    net_90: 'Net 90',
    custom: 'Custom',
  },

  labels: {
    creditLimit: 'Credit Limit',
    currentBalance: 'Balance',
    availableCredit: 'Available',
    overdue: 'Overdue',
    utilization: 'Credit Utilization',
    lastPayment: 'Last Payment',
    noPayments: 'No payments yet',
    accountDetails: 'Account Details',
    accountInfo: 'Account Information',
    tenant: 'Tenant',
    paymentTerms: 'Payment Terms',
    vibanNumber: 'VIBAN Number',
    creditManagement: 'Credit Management',
    newCreditLimit: 'New Credit Limit',
    hasOverdue: 'Overdue',
    notes: 'Notes',
    sapBpNumber: 'SAP BP',
  },

  // Table headers if applicable
  table: {
    account: 'Account',
    tenant: 'Tenant',
    channel: 'Channel',
    paymentTerms: 'Payment Terms',
    creditLimit: 'Credit Limit',
    currentBalance: 'Balance',
    overdue: 'Overdue',
    actions: 'Actions',
    name: 'Name',
    status: 'Status',
  },

  view: {
    table: 'Table View',
    cards: 'Card View',
  },

  empty: {
    noAccountsFound: 'No accounts found',
  },

  cityAccess: {
    title: 'City Access',
    description: 'Select which cities this account can place orders to. Leave empty to allow all cities.',
    noCitiesAvailable: 'No active cities available',
    allCitiesAllowed: 'All cities allowed',
  },

  // Region access control
  regionAccess: 'Region Access',
  regionAccessDescription: 'Select which regions this account can deliver to. Leave empty to allow all regions.',
  noRegionsAvailable: 'No active regions available',
  allRegionsAllowed: 'All regions allowed',
}
